"use client";

import { useEffect, useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import AuthService from "../../services/authService";
import { supabase } from "../../lib/supabase";
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Link,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Logo from "../../assets/LogoPdf.png";
import BackgroundImage from "../../assets/background.jpg";

const MIN_LENGTH = 8;

const ResetPassword = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  // null = verificando link; true = sesión de recovery OK; false = link inválido/expirado
  const [ready, setReady] = useState(null);
  // Capturado en el PRIMER render, antes de que supabase procese/limpie el hash:
  // marca si llegamos desde un link de recovery (no desde una sesión persistida).
  const [hadRecoveryLink] = useState(() => {
    if (typeof window === "undefined") return false;
    const hash = window.location.hash || "";
    const search = window.location.search || "";
    return (
      hash.includes("type=recovery") ||
      hash.includes("access_token") ||
      hash.includes("error") ||
      /[?&]code=/.test(search)
    );
  });
  // Distingue el primer set-password de una invitación ("Invite user") del
  // recovery normal, para adaptar los textos. El link de invite trae type=invite.
  const [isInvite] = useState(() => {
    if (typeof window === "undefined") return false;
    const hash = window.location.hash || "";
    const search = window.location.search || "";
    return hash.includes("type=invite") || /[?&]type=invite/.test(search);
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const handleCloseSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  // Al volver del mail, supabase-js parsea el hash (#access_token...&type=recovery)
  // y establece una sesión de recovery, emitiendo el evento PASSWORD_RECOVERY.
  // Aceptamos SOLO ese flujo: el evento, o (por si ya disparó antes de suscribir)
  // una sesión que provenga de un link de recovery. Una sesión persistida normal
  // (usuario ya logueado, sin link) NO habilita el form.
  useEffect(() => {
    let active = true;

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active) return;
      if (event === "PASSWORD_RECOVERY") setReady(true);
      else if (session && hadRecoveryLink) setReady(true);
    });

    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      if (data?.session && hadRecoveryLink) setReady(true);
    });

    // Si en unos segundos no hubo recovery válido, el link es inválido o expiró.
    const timer = setTimeout(() => {
      if (active) setReady((prev) => (prev === null ? false : prev));
    }, 4000);

    return () => {
      active = false;
      clearTimeout(timer);
      sub?.subscription?.unsubscribe();
    };
  }, [hadRecoveryLink]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < MIN_LENGTH) {
      setSnackbar({
        open: true,
        message: `La contraseña debe tener al menos ${MIN_LENGTH} caracteres.`,
        severity: "error",
      });
      return;
    }
    if (password !== confirm) {
      setSnackbar({ open: true, message: "Las contraseñas no coinciden.", severity: "error" });
      return;
    }

    setLoading(true);
    try {
      await AuthService.updatePassword(password);
      AuthService.singOut();
      setSnackbar({
        open: true,
        message: isInvite
          ? "¡Listo! Tu cuenta quedó activa. Iniciá sesión con tu contraseña."
          : "Contraseña actualizada. Iniciá sesión con tu nueva contraseña.",
        severity: "success",
      });
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "No pudimos actualizar la contraseña. El enlace pudo haber expirado.";
      setSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "#f8f9fa",
      transition: "all 0.2s ease",
      "& fieldset": { borderColor: "#e0e0e0", transition: "border-color 0.2s ease" },
      "&:hover fieldset": { borderColor: "#b0b0b0" },
      "&.Mui-focused fieldset": { borderColor: "#2563EB", borderWidth: "2px" },
      "&.Mui-focused": { backgroundColor: "#fff" },
    },
    "& .MuiInputLabel-root": { color: "#888", "&.Mui-focused": { color: "#2563EB" } },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        backgroundImage: `url(${BackgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        position: "relative",
      }}
    >
      <Box
        sx={{ position: "absolute", inset: 0, backgroundColor: "rgba(0, 0, 0, 0.4)", zIndex: 0 }}
      />

      <Box
        sx={{
          flex: 1,
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
          zIndex: 1,
          px: 6,
        }}
      >
        <Box sx={{ textAlign: "center", maxWidth: 420 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: "#fff",
              mb: 2,
              letterSpacing: "-0.02em",
              textShadow: "0 2px 12px rgba(0,0,0,0.3)",
            }}
          >
            Nueva contraseña
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "rgba(255,255,255,0.85)",
              fontWeight: 400,
              lineHeight: 1.6,
              textShadow: "0 1px 6px rgba(0,0,0,0.2)",
            }}
          >
            Elegí una contraseña segura para volver a entrar a tu cuenta.
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          width: { xs: "100%", md: 480 },
          minWidth: { md: 480 },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
          zIndex: 1,
          backgroundColor: "rgba(255, 255, 255, 0.97)",
          backdropFilter: "blur(20px)",
          px: { xs: 3, sm: 5 },
          py: 4,
          boxShadow: "-8px 0 40px rgba(0,0,0,0.12)",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 380 }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <img src={Logo} alt="Voras" style={{ height: 56 }} />
          </Box>

          <Typography
            variant="h5"
            sx={{ textAlign: "center", fontWeight: 700, color: "#1a1a1a", mb: 0.5 }}
          >
            {isInvite ? "Creá tu contraseña" : "Restablecer contraseña"}
          </Typography>

          {ready === false ? (
            <>
              <Typography
                variant="body2"
                sx={{ textAlign: "center", color: "#888", mt: 1, mb: 3, fontSize: "0.9rem" }}
              >
                {isInvite
                  ? "El enlace de invitación es inválido o expiró. Pedile al administrador que te reenvíe la invitación."
                  : "El enlace de recuperación es inválido o expiró. Volvé a solicitarlo."}
              </Typography>
              <Box sx={{ textAlign: "center" }}>
                <Link
                  component={RouterLink}
                  to="/forgot-password"
                  underline="hover"
                  sx={{ color: "#2563EB", fontWeight: 600, fontSize: "0.9rem" }}
                >
                  Solicitar un nuevo enlace
                </Link>
              </Box>
            </>
          ) : ready === null ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} sx={{ color: "#2563EB" }} />
            </Box>
          ) : (
            <>
              <Typography
                variant="body2"
                sx={{ textAlign: "center", color: "#888", mb: 3, fontSize: "0.9rem" }}
              >
                {isInvite
                  ? `¡Bienvenido a Voras! Definí tu contraseña para ingresar (mínimo ${MIN_LENGTH} caracteres).`
                  : `Escribí tu nueva contraseña (mínimo ${MIN_LENGTH} caracteres).`}
              </Typography>

              <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: "100%" }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: "#555", mb: 0.5, ml: 0.5, display: "block" }}
                >
                  Nueva contraseña
                </Typography>
                <TextField
                  fullWidth
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  size="small"
                  sx={{ ...inputSx, mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon sx={{ color: "#999", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((v) => !v)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? (
                            <VisibilityOff sx={{ fontSize: 20 }} />
                          ) : (
                            <Visibility sx={{ fontSize: 20 }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: "#555", mb: 0.5, ml: 0.5, display: "block" }}
                >
                  Repetir contraseña
                </Typography>
                <TextField
                  fullWidth
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  size="small"
                  sx={{ ...inputSx, mb: 3 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon sx={{ color: "#999", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  disableElevation
                  sx={{
                    borderRadius: "12px",
                    padding: "12px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    backgroundColor: "#2563EB",
                    mb: 2,
                    "&:hover": { backgroundColor: "#1D4ED8" },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={22} color="inherit" />
                  ) : (
                    "Guardar contraseña"
                  )}
                </Button>

                <Box sx={{ textAlign: "center" }}>
                  <Link
                    component={RouterLink}
                    to="/login"
                    underline="hover"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      color: "#2563EB",
                      fontWeight: 600,
                      fontSize: "0.9rem",
                    }}
                  >
                    <ArrowBackIcon sx={{ fontSize: 18 }} />
                    Volver al inicio de sesión
                  </Link>
                </Box>
              </Box>
            </>
          )}
        </Box>

        <Typography
          variant="caption"
          sx={{ position: "absolute", bottom: 20, color: "#bbb", fontSize: "0.7rem" }}
        >
          © {new Date().getFullYear()} Voras · Todos los derechos reservados
        </Typography>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === "error" ? 6000 : 8000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: "10px", fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ResetPassword;
